import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as api from '../../api';

import * as FormUtils from '../../utils/form';
import * as rootReducer from '../../reducers/root';

import { Button, Size as ButtonSize } from '../Button';
import { sendEventMessage, Message } from '../../utils/native';
import { getFormPath, getReviewPath } from '../../utils/form';
import { Close, Icon } from '../Icon';
import { Status } from '../../scenes/FormStart/IntroPage';
import { PatientRouteParams } from '../../scenes/router';

import styles from './index.module.css';

///////////////////////////////////////////////////
////// TYPES

export interface BaseItem {
  id: string;
  primaryLabel: string;
  secondaryLabel?: string | React.ReactNode[];
  buttonText?: string;
  buttonInvertColors?: boolean;
  isDisabled?: boolean;
  canRemove?: boolean;
  children?: React.ReactNode;
  previsitFormSetId?: string;
  form?: api.PrevisitForm.PrevisitForm;
}

export enum Size {
  Small = 'PrimaryValueSmall',
  Medium = 'PrimaryValueMedium',
  Large = 'PrimaryValueLarge',
}

interface DispatchProps {
  setFormStatus: (
    formSetId: string,
    formId: string,
    status: api.PrevisitForm.Status,
  ) => void;
}

interface Props<T extends BaseItem>
  extends DispatchProps,
    RouteComponentProps<PatientRouteParams> {
  authenticationCode?: string;
  encounter?: api.Encounter.Encounter;
  items: T[];
  compact: boolean;
  primaryLabelSize: Size;
  secondaryLabelSize?: Size;
  deleteItem?: (id: string) => void;
  selectItemIndex?: (index: number) => void;
  className?: string;
  closeIconTitle?: string;
  multiFormSet?: boolean;
}

///////////////////////////////////////////////////
////// COMPONENT

class ItemsListComponent<T extends BaseItem> extends React.Component<
  Props<T>,
  never
> {
  render() {
    const {
      authenticationCode,
      encounter,
      items,
      className,
      primaryLabelSize,
      secondaryLabelSize,
      deleteItem,
      selectItemIndex,
      compact,
      history,
      setFormStatus,
      multiFormSet,
      closeIconTitle,
    } = this.props;

    const deleteIcon = (itemId: string) =>
      deleteItem && (
        <Icon
          title={closeIconTitle}
          className={styles.DeleteIcon}
          icon={Close}
          size={1}
          color={'#FF0000'}
          onClick={() => deleteItem(itemId)}
        />
      );
    const selectButton = (item: T, index: number) => {
      if (!item.buttonText || !authenticationCode) return null;

      const buttonProps = {
        className: styles.SelectItem,
        disabled: item.isDisabled,
        size: ButtonSize.Small,
        children: item.buttonText,
        invertColors: item.buttonInvertColors,
      };
      if (selectItemIndex) {
        return (
          <div>
            <Button {...buttonProps} onClick={() => selectItemIndex(index)} />
          </div>
        );
      }

      const onClick = async () => {
        sendEventMessage(Message.ActiveAction);
        if (
          item.buttonText === Status.Start &&
          !FormUtils.isDocumentForm(item.id)
        ) {
          await FormUtils.maybeUpdateFormStatus(
            item.id,
            api.PrevisitForm.Status.Started,
          );
        }
        if (item.buttonText === Status.Start && item.previsitFormSetId) {
          setFormStatus(
            item.previsitFormSetId,
            item.id,
            api.PrevisitForm.Status.Started,
          );
        }
        if (item.previsitFormSetId) {
          if (item.form && item.form.isReviewable) {
            history.push(
              getReviewPath({
                authenticationCode,
                formSetId: item.previsitFormSetId,
                formId: item.id,
                multi: multiFormSet,
              }),
            );
          } else {
            history.push({
              pathname: getFormPath({
                authenticationCode,
                formSetId: item.previsitFormSetId,
                formId: item.id,
                multi: multiFormSet,
              }),
              search: `?page=1`,
              state: { encounter, form: item },
            });
          }
        }
      };

      return (
        <div>
          <div onClick={onClick}>
            <Button
              className={styles.SelectItem}
              disabled={item.isDisabled}
              size={ButtonSize.Small}
              children={item.buttonText}
              invertColors={item.buttonInvertColors}
              data-testid={`start-form-btn-${item.id}`}
            />
          </div>
        </div>
      );
    };

    return (
      <div className={[styles.ItemsList, className || ''].join(' ')}>
        {items.map((item, index) => (
          <div
            className={`${styles.Item} ${compact ? styles.Compact : ''}`}
            key={index}
          >
            <div className={styles.ItemInfo}>
              <div className={styles.Values}>
                <div
                  className={[
                    styles.PrimaryValue,
                    styles[primaryLabelSize],
                  ].join(' ')}
                >
                  {item.primaryLabel}
                </div>
                {item.secondaryLabel && secondaryLabelSize ? (
                  <div
                    className={[
                      styles.SecondaryValue,
                      styles[secondaryLabelSize],
                    ].join(' ')}
                  >
                    {item.secondaryLabel}
                  </div>
                ) : null}
              </div>
              {selectButton(item, index)}
              {item.canRemove && deleteIcon(item.id)}
            </div>
            {item.children || null}
          </div>
        ))}
      </div>
    );
  }
}

export const ItemsListWithRouter = withRouter(ItemsListComponent);

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormStatus: (
      formSetId: string,
      formId: string,
      status: api.PrevisitForm.Status,
    ) => dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
  };
}

export const ItemsList = connect(
  undefined,
  mapDispatchToProps,
)(ItemsListWithRouter);
